import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams } from 'react-router-dom'

import { StickyBox } from 'components/Box'
import { CommonFormContextProps, SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import Tabs, { getPage, Tab } from 'components/Tabs'
import {
  DefaultsContext,
  DefaultsContextType,
} from 'features/globalSetup/pages/Defaults/DefaultsContext'
import { WorkspacePrebidConfiguration } from 'features/globalSetup/types/workspacePrebidConfiguration'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { DefaultsTab, DefaultsUrlParams, useTabs } from './useTabs'

export const DefaultsPage = (): React.JSX.Element => {
  const { t } = useTranslation(['features/globalSetup', 'common'])
  const ability = useAbility()
  const tabs: Tab<DefaultsTab>[] = useTabs()
  const { tab: currentTab, workspaceId } = useParams<DefaultsUrlParams>()
  const [workspaceConfig, setWorkspaceConfig] = useState<WorkspacePrebidConfiguration>()

  const commonContextProps: CommonFormContextProps = useCommonFormContextProps({
    formId: 'global-setup-defaults-form',
  })
  const contextProps: DefaultsContextType = {
    ...commonContextProps,
    prebidStack: {
      setWorkspaceConfig,
      workspaceConfig,
    },
  }

  if (!workspaceId || !currentTab) {
    return <Navigate to={ROUTES.BASE.NOT_FOUND} />
  }

  return (
    <DefaultsContext.Provider value={contextProps}>
      <StickyBox>
        <DefaultsContext.Consumer>
          {(ctxProps: DefaultsContextType): React.JSX.Element => (
            <PageHeader
              actions={
                <SaveButton
                  {...ctxProps}
                  can={ability.can('update', 'GlobalSetupFeature-defaults-prebidStack')}
                />
              }
              title={t('defaults.header')}
            />
          )}
        </DefaultsContext.Consumer>

        <Tabs
          path={ROUTES.GLOBAL_SETUP.DEFAULTS.ROOT}
          tabs={tabs}
        />
      </StickyBox>

      {getPage<DefaultsTab>(tabs, currentTab, 'prebid-stack')}
    </DefaultsContext.Provider>
  )
}
